import React from "react";

import { Promised } from "../../types";

import { useAppDispatch } from "@SonarCX/hooks/redux";
import { useEnvironment } from "@SonarCX/providers/EnvironmentProvider";
import { AddErrorAlert, AddSuccessAlert } from "@SonarCX/slices/alert";
import {
  deleteTaskCategoryRule,
  getTaskCategoryRules,
  upsertTaskCategoryRule,
} from "@SonarCX/utilities/supabase";

export type TaskCategoryRule = Promised<typeof getTaskCategoryRules>[0];

const useTaskCategoryRules = () => {
  const dispatch = useAppDispatch();
  const { loaded } = useEnvironment();

  const [rules, setRules] = React.useState<TaskCategoryRule[]>([]);

  React.useEffect(() => {
    if (!loaded) {
      return;
    }

    const loadRules = async () => {
      const fetchedRules = await getTaskCategoryRules();
      setRules(fetchedRules);
    };

    loadRules();
  }, [loaded]);

  const showError = React.useCallback(
    (message) => dispatch(AddErrorAlert({ err: { message } })),
    [dispatch]
  );

  const showSuccess = React.useCallback(
    (message) => dispatch(AddSuccessAlert(message)),
    [dispatch]
  );

  const upsertRule = React.useCallback(
    async ({
      id,
      categoryId,
      types,
    }: {
      id?: number;
      categoryId: string;
      types: string[];
    }) => {
      const updatedRule = await upsertTaskCategoryRule({
        id,
        categoryId,
        types,
      });
      if (!updatedRule) {
        showError("Failed to save task category rule.");
        return;
      }

      setRules((prevRules) => {
        if (!id) {
          return [...prevRules, updatedRule];
        }

        return prevRules.map((rule) => (rule.id === id ? updatedRule : rule));
      });

      showSuccess("Task category rule saved successfully.");
    },
    [showError, showSuccess]
  );

  const deleteRule = React.useCallback(
    async (id: number) => {
      const success = await deleteTaskCategoryRule(id);
      if (!success) {
        showError("Failed to delete task category rule.");
        return;
      }

      setRules((prevRules) => prevRules.filter((rule) => rule.id !== id));
      showSuccess("Task category rule deleted successfully.");
    },
    [showError, showSuccess]
  );

  const applyTaskCategoryRules = React.useCallback(
    (categoryId: String, types: TaskType[]) => {
      if (!categoryId) {
        return types;
      }

      const rule = rules.find((rule) => rule.task_category_id === categoryId);
      if (!rule) {
        return types;
      }

      return types.filter((type) => rule.task_type_ids.includes(type.id));
    },
    [rules]
  );

  return { rules, upsertRule, deleteRule, applyTaskCategoryRules };
};

export default useTaskCategoryRules;
